import { fetchAuth } from "./api";

const store = new Map();
const inflight = new Map();

export const SESSION_JSON_TTL_MS = 30 * 60_000;
export const STATS_JSON_TTL_MS = 10 * 60_000;

export class CachedFetchError extends Error {
  constructor(status, message, body = null) {
    super(message);
    this.name = "CachedFetchError";
    this.status = status;
    this.body = body;
  }
}

function cacheKey(path) {
  return String(path || "");
}

function repairJson(text) {
  if (!text) return null;
  try {
    return JSON.parse(text);
  } catch (error) {
    const repaired = text
      .replace(/\bNaN\b/g, "null")
      .replace(/-Infinity/g, "null")
      .replace(/\bInfinity\b/g, "null");
    try {
      return JSON.parse(repaired);
    } catch {
      throw error;
    }
  }
}

async function readJson(response) {
  const text = await response.text();
  return repairJson(text);
}

/**
 * In-memory GET JSON cache with in-flight dedupe.
 * Historical / static payloads can use a long TTL.
 * Dynamic payloads should use a short TTL and `force: true` to revalidate.
 */
export async function fetchJsonCached(path, {
  ttlMs = 60_000,
  force = false,
  timeoutMs,
  signal,
} = {}) {
  const key = cacheKey(path);
  if (!key) throw new Error("fetchJsonCached: missing path");

  if (!force) {
    const hit = store.get(key);
    if (hit && hit.expiresAt > Date.now()) {
      return hit.data;
    }
  }

  if (inflight.has(key)) {
    return inflight.get(key);
  }

  const request = (async () => {
    const response = await fetchAuth(path, { timeoutMs, signal });
    if (!response?.ok) {
      let body = null;
      try {
        body = await readJson(response);
      } catch {
        body = null;
      }
      const message = body?.error || `Failed ${path} (${response?.status || 0})`;
      throw new CachedFetchError(response?.status || 0, message, body);
    }
    const data = await readJson(response);
    store.set(key, {
      data,
      fetchedAt: Date.now(),
      expiresAt: Date.now() + Math.max(0, ttlMs),
    });
    return data;
  })();

  inflight.set(key, request);
  try {
    return await request;
  } finally {
    if (inflight.get(key) === request) inflight.delete(key);
  }
}

export function peekCachedJson(path) {
  const hit = store.get(cacheKey(path));
  return hit ? hit.data : undefined;
}

export function peekCachedEntry(path) {
  return store.get(cacheKey(path)) || null;
}

export function setCachedJson(path, data, ttlMs = 60_000) {
  const key = cacheKey(path);
  store.set(key, {
    data,
    fetchedAt: Date.now(),
    expiresAt: Date.now() + Math.max(0, ttlMs),
  });
  return data;
}

export function invalidateJsonCache(prefix = "") {
  if (!prefix) {
    store.clear();
    inflight.clear();
    return;
  }
  for (const key of store.keys()) {
    if (key.startsWith(prefix)) store.delete(key);
  }
  for (const key of inflight.keys()) {
    if (key.startsWith(prefix)) inflight.delete(key);
  }
}
